import { atom } from "jotai";

/**
 * Reporter atoms for ReporterEditorPanel
 *
 * Consolidates the 18+ useState declarations in ReporterEditorPanel
 * into organized atom groups:
 * - Operation state (mutually exclusive - only one operation at a time)
 * - Error state (consolidated with type discrimination)
 * - Config state (user preferences)
 */

// =============================================================================
// Operation State
// =============================================================================

/**
 * Operation state - only one operation can run at a time.
 * Using discriminated union prevents invalid states like
 * isStructuring && isModifying being true simultaneously.
 */
export type ReporterOperation =
  | { type: "idle" }
  | { type: "structuring" }
  | { type: "modifying" }
  | { type: "transforming" }
  | { type: "saving" }
  | { type: "signing" };

export const reporterOperationAtom = atom<ReporterOperation>({ type: "idle" });

// Derived: Check if any operation is running
export const isOperationRunningAtom = atom((get) => get(reporterOperationAtom).type !== "idle");

// =============================================================================
// Error State
// =============================================================================

/**
 * Consolidated error state with type discrimination.
 * Replaces 5 separate error strings (saveError, structuringError, etc.)
 */
export type ReporterError = {
  type: "save" | "structure" | "modify" | "transform" | "sign";
  message: string;
} | null;

export const reporterErrorAtom = atom<ReporterError>(null);

// =============================================================================
// Config State
// =============================================================================

// Auto-structure: automatically structure report after transcription
export const autoStructureEnabledAtom = atom(false);

// Auto-go-to-slice: navigate to finding location when clicking in report
export const autoGoToSliceOnFocusEnabledAtom = atom(true);

// Auto-save: automatically save report changes
export const autoSaveEnabledAtom = atom(true);

// =============================================================================
// Status State
// =============================================================================

// Auto-save status message (e.g., "Saved", "Saving...", "Error saving")
export const autoSaveStatusAtom = atom<string>("");
